import { Store } from "@ngxs/store";
import { interval, of } from "rxjs";
import { Injectable } from "@angular/core";
import { catchError, switchMap, tap } from "rxjs/operators";

import {
	ConfigurationState,
	type ConfigurationStateModel,
} from "../configuration/state/configuration.state";

import {
	LoadBookingsOfCurrentDay,
	RecalculateRoomAvailabilityStatus,
} from "./state/booking.actions";

import type { OnDestroy } from "@angular/core";
import type { Subscription } from "rxjs";

@Injectable({
	providedIn: "root",
})
export class BookingService implements OnDestroy {
	private bookingDataFetchPollingSubscription: Subscription;
	private statusUpdatePollingSubscription: Subscription;

	constructor(private store: Store) {}

	ngOnDestroy(): void {
		if (this.bookingDataFetchPollingSubscription) {
			this.bookingDataFetchPollingSubscription.unsubscribe();
		}

		if (this.statusUpdatePollingSubscription) {
			this.statusUpdatePollingSubscription.unsubscribe();
		}
	}

	public initBookingFetchInterval() {
		const configState = this.store.selectSnapshot<ConfigurationStateModel>(
			ConfigurationState.state,
		);

		if (!configState.dsConfiguration) {
			return;
		}

		if (this.bookingDataFetchPollingSubscription) {
			this.bookingDataFetchPollingSubscription.unsubscribe();
		}

		const DEFAULT_REFRESH_INTERVAL = 5; // 5 minutes
		const MILLIS_PER_MIN = 60 * 1000;

		const dsConfigRefreshMin = configState.dsConfiguration.viewRefreshInterval;
		const refreshMinutes = dsConfigRefreshMin >= 1 ? dsConfigRefreshMin : DEFAULT_REFRESH_INTERVAL;
		const refreshIntervalInMilis = refreshMinutes * MILLIS_PER_MIN;
		this.bookingDataFetchPollingSubscription = interval(refreshIntervalInMilis)
			.pipe(
				switchMap(() => {
					return this.store.dispatch(new LoadBookingsOfCurrentDay());
				}),
				catchError((e) => {
					console.error("Error while loading bookings of current day.", e);
					return of(void 0);
				}),
				tap(() => {
					this.initBookingFetchInterval();
				}),
			)
			.subscribe();
	}

	public initStatusUpdateInterval() {
		const configState = this.store.selectSnapshot<ConfigurationStateModel>(
			ConfigurationState.state,
		);

		if (!configState.dsConfiguration) {
			return;
		}

		if (this.statusUpdatePollingSubscription) {
			this.statusUpdatePollingSubscription.unsubscribe();
		}

		const FREE_BUSY_POLLING_INTERVAL = 10000; // 10 seconds
		this.statusUpdatePollingSubscription = interval(FREE_BUSY_POLLING_INTERVAL)
			.pipe(
				switchMap(() => {
					return this.store.dispatch(new RecalculateRoomAvailabilityStatus());
				}),
				catchError((e) => {
					console.error("Error while updating free busy state.", e);
					return of(void 0);
				}),
			)
			.subscribe();
	}
}
